import React, {FC, ReactElement, useMemo, useState} from 'react';
import {DataTableCard} from "../../../components/DataTable/DataTableCard";
import {ColumnsType} from "antd/lib/table";
import {Button, Popover, Space, Typography} from "antd";
import {EyeOutlined} from "@ant-design/icons";
import {DashboardView, StudentExercisePerformance} from "../../../types";
import {DATETIME_FORMAT} from "../../../constants";
import {SearchField} from "../../../components/DataTable/SearchField";
import {ExportButton} from "../../../components/DataTable/ExportButton";
import {FilterButton} from "../../../components/DataTable/FilterButton";
import {Filter, QueryFilter} from "../../../components/DataTable/Filters/types";
import {NumericFilter} from "../../../components/DataTable/Filters/NumericFilter";
import moment from "moment";
import {DateFilter} from "../../../components/DataTable/Filters/DateFilter";
import {Translate, useLabels} from "../../../hooks/Labels";
import isNumeric from "antd/lib/_util/isNumeric";
import {useOrganisation} from "../../../layout/OrganisationInformationProvider";
import { SectionPerformanceRadarPlot } from '../../../components/DashboardViews/components/Generic/SectionPerformanceRadarPlotCard';
import { useSectionPerformanceFirstBestData } from '../../../components/DashboardViews/components/Generic/useSectionPerformanceFirstBestData';
import {PreserveQueryLink as Link} from "../../../components/PreserveQueryLink";

interface Props {
    activityId?: string;
}

const {Text} = Typography;

const isConversationSectionPerformance = (record: StudentExercisePerformance): boolean => {
    const dashboardViews = (record.activity?.renderers || []).map(renderer => renderer.dashboard_view);
    if (
        dashboardViews.includes(DashboardView.EXERCISE_TRANSCRIPT_COVERAGE)
        || dashboardViews.includes(DashboardView.EXERCISE_CONVERSATION_STORY_COVERAGE)
        || dashboardViews.includes(DashboardView.EXERCISE_CHECKLIST_COVERAGE)
        || dashboardViews.includes(DashboardView.EXERCISE_CONVERSATION_ELICIT_PERFORMANCE)
    ) {
        return true;
    }
    return false;
};

const StudentImprovementRadarTooltip: FC<{record: StudentExercisePerformance, children: React.ReactNode}> = ({record, children}) => {
    const [shouldPrefetch, setShouldPrefetch] = useState(false);

    const commonFilters = useMemo(() => ({
        student: record.student.id,
    }), [record.student.id]);

    useSectionPerformanceFirstBestData({
        type: 'conversation',
        activityId:record.activity.id.toString(),
        defaultFilters: commonFilters,
        enabled: shouldPrefetch,
    });

    return (
        <Popover
            trigger={['click']}
            content={
                <div style={{width: 900, height: 450}}>
                    <SectionPerformanceRadarPlot 
                        type='conversation' 
                        activityId={record.activity.id.toString()} 
                        defaultFilters={commonFilters}
                    />
                </div>
            }
        >
            <span onMouseEnter={() => setShouldPrefetch(true)}>{children}</span>
        </Popover>
    );
};

const filters = (canViewIndividualData: boolean): Filter<any>[] => [
    ...(canViewIndividualData ? [{
        attribute: 'first_score',
        title: 'First Score',
        component: NumericFilter,
        config: {
            filters: ['exact', 'range', 'lt', 'gt'],
            unit: '%',
        },
    },
    {
        attribute: 'best_score',
        title: 'Best Score',
        component: NumericFilter,
        config: {
            filters: ['exact', 'range', 'lt', 'gt'],
            unit: '%',
        },
    },
    {
        attribute: 'improvement_delta',
        title: 'Improvement Delta',
        component: NumericFilter,
        config: {
            filters: ['exact', 'range', 'lt', 'gt'],
            unit: '%',
        },
    }] : []) as Filter<any>[],
    {
        attribute: 'attempts',
        title: 'Attempts',
        component: NumericFilter,
        config: {
            filters: ['exact', 'range', 'lt', 'gt']
        },
    },
    {
        attribute: 'last_attempt_datetime',
        title: 'Last Attempted',
        component: DateFilter,
        config: {
            filters: ['lt', 'gt']
        },
    },
];

const columns = (t: Translate, canViewIndividualData: boolean, canViewUserPii: boolean): ColumnsType<StudentExercisePerformance> => [
    ...(canViewUserPii ? [{
        title: 'Name',
        dataIndex: ['student', 'full_name'],
        key: 'student.full_name',
        render: (text, record) => <Link to={`/students/${record.student.id}/activity/${record.id}`}>{text}</Link>
    },
    {
        title: 'Email',
        dataIndex: ['student', 'email'],
        key: 'student.email',
    }] : [{
        title: 'Student',
        dataIndex: ['student', 'id'],
        key: 'student.id',
        render: (value, record) => <Link to={`/students/${record.student.id}/activity/${record.id}`}>{`${t('student')} ${value}`}</Link>
    }]) as ColumnsType<StudentExercisePerformance>,
    ...(canViewIndividualData ? [{
        title: 'Score',
        key: 'score',
        children: [
            {
                title: 'First',
                dataIndex: 'first_score',
                key: 'first_score',
                width: '4.5rem',
                sorter: true,
                render: (value, record) => isNumeric(value) ? <Text style={{fontSize: '1rem'}}>{value}%</Text> : 'N/A'
            },
            {
                title: 'Best',
                dataIndex: 'best_score',
                key: 'best_score',
                width: '4.5rem',
                sorter: true,
                render: (value, record) =>
                    isNumeric(value) ? <Text strong style={{fontSize: '1rem'}}>
                        {value}%
                    </Text> : 'N/A'
            },
            {
                title: 'Improved',
                dataIndex: 'improvement_delta',
                key: 'improvement_delta',
                width: '9rem',
                sorter: true,
                render: (value, record) => 
                    isNumeric(value) 
                        ? (
                            <div style={{display:'flex', justifyContent:'space-between'}}>
                            {   
                                value > 0 
                                ? <Text strong style={{fontSize: '1rem'}} type='success'>▲ {value}%</Text>
                                : '-'
                            }
                            
                            {
                                isConversationSectionPerformance(record)
                                && (
                                    <StudentImprovementRadarTooltip record={record}>
                                        <Button size='small' icon={<EyeOutlined/>}>
                                            View
                                        </Button>
                                    </StudentImprovementRadarTooltip>
                            )}
                            </div> 
                        )
                        : 'N/A'

            }
        ]
    }] : []) as ColumnsType<StudentExercisePerformance>,
    {
        title: 'Last Attempted',
        dataIndex: 'last_attempt_datetime',
        key: 'last_attempt_datetime',
        sorter: true,
        responsive: ['lg'],
        render: (value) => moment(value).format(DATETIME_FORMAT)
    },
    {
        title: 'Attempts',
        dataIndex: 'attempts',
        sorter: true,
        key: 'attempts'
    },
]

export const StudentPerformanceDataTableCard: FC<Props> = ({activityId}): ReactElement => {
    const {t} = useLabels();
    const {
        can_view_individual_data: canViewIndividualData,
        can_view_user_pii: canViewUserPii
    } = useOrganisation();
    const tableFilters = filters(canViewIndividualData);
    const tableColumns = columns(t, canViewIndividualData, canViewUserPii);
    return (
        <DataTableCard
            title={<Space size='large'>
                <Text>{t('student')} Performance</Text>
                {canViewUserPii && <SearchField placeholder={`${t('student')} name or email`}/>}
            </Space>}
            extra={<Space>
                <ExportButton
                    url='/reports/student-exercise-performance/'
                    defaultFilters={{
                        activity: activityId || '',
                        exclude: 'activity.renderers,activity.primary_attribute,activity.type'
                    }}
                    filenamePrefix={`${t('student', false)}-performance`}
                    searchParamFilters={[QueryFilter.COHORTS]}
                />
                <FilterButton filters={tableFilters}/>
            </Space>}
            dataTable={{
                columns: tableColumns,
                defaultFilters: {
                    activity: activityId || '',
                },
                searchParamFilters: [QueryFilter.COHORTS],
                query: '/reports/student-exercise-performance/'
            }}/>
    );
};
